import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCamera, faFile, faImage, faMicrophone, faPaperclip, faPause, faPlay, faTrash } from "@fortawesome/free-solid-svg-icons";

import HttpManager from "../../../services/HttpManager";
import { errorTexto } from "./salesHelpers";
import { prepararSonido } from "./avisoMatriculaGratis";
import { BandejaEnvio, cambiarElegido, Elegido as ElegidoVarios, MAX_ARCHIVOS, enviarVarios, tipoDeArchivo } from "./equipoMedios";
import { comprimirFoto, MarcarFoto } from "./marcarFoto";
import WappIcono from "../../../components/wappIcono";
import { BotonEmoji, insertarEnCursor, teclaEnter } from "./equipoEmojis";
import { useCrecer } from "./crecer";

/**
 * Archivos por WappChat (Esteban, 2026-10-04): clip (cámara, foto o video, documento) con vista previa y texto opcional,
 * y micrófono para notas de voz. El servidor convierte todo a lo que pide WhatsApp (nota de voz OGG/Opus, video MP4
 * H.264 de hasta 16 MB, foto JPG), así al cliente le llega como nota de voz y como video, no como archivo.
 * Nota de voz como en WhatsApp (Esteban, 2026-10-14): nunca sale sola; se pausa, se escucha, se sigue grabando o se borra.
 */

export type Kind = "voice" | "media" | "document";
interface Elegido { file: Blob; name: string; kind: Kind; url: string | null; tipo: "image" | "video" | "audio" | "document"; listo?: boolean; }

/** nginx del servidor deja subir hasta esto (client_max_body_size). */
export const MAX_SUBIDA = 200 * 1024 * 1024;

const tamano = (b: number) => (b >= 1048576 ? (b / 1048576).toFixed(1) + " MB" : Math.max(1, Math.round(b / 1024)) + " KB");
const reloj = (s: number) => Math.floor(s / 60) + ":" + String(Math.floor(s % 60)).padStart(2, "0");
const esTelefono = () => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(pointer: coarse)").matches;

const tipoDe = (f: File, kind: Kind): Elegido["tipo"] => {
	if (kind === "document") return "document";
	const n = f.name.toLowerCase();
	if (f.type.startsWith("video/") || /\.(mov|mp4|m4v|3gp|webm|mkv|avi)$/.test(n)) return "video";
	return "image";
};

/** El formato que el navegador sabe grabar: Chrome/Android webm-opus, Safari/iPhone mp4-aac. */
const formatoGrabacion = (): { mime: string; ext: string } => {
	const MR: any = (window as any).MediaRecorder;
	for (const [mime, ext] of [["audio/webm;codecs=opus", "webm"], ["audio/ogg;codecs=opus", "ogg"], ["audio/mp4", "m4a"], ["audio/webm", "webm"]]) {
		if (MR && MR.isTypeSupported && MR.isTypeSupported(mime)) return { mime, ext };
	}
	return { mime: "", ext: "m4a" };
};

interface Props {
	campusId: number;
	prospectId: number;
	channelId: number | null;
	puede: boolean;              // ventana abierta y número listo
	onEnviado: () => void;
	/** El compositor esconde el cuadro de texto mientras se graba o se ve la vista previa. */
	onOcupado: (ocupado: boolean) => void;
	/** Equipo: en vez de mandarlo por WhatsApp, lo sube a la conversación interna. */
	enviar?: (file: Blob, name: string, kind: Kind, caption: string, onProgress: (f: number) => void) => Promise<void>;
	/** WappTeam: el clip y su menú son los mismos que en WappChat, pero los archivos elegidos van a su propia bandeja. */
	alElegir?: (archivos: File[]) => void;
	/** El texto de la foto le puso costo a la matrícula: ya salió, y el compositor enseña el aviso rojo. */
	onMatricula?: (fragmento: string) => void;
}

const Adjuntos: React.FC<Props> = ({ campusId, prospectId, channelId, puede, onEnviado, onOcupado, enviar, alElegir, onMatricula }) => {
	const http = HttpManager.getInstance();
	const [menu, setMenu] = useState(false);
	const [elegido, setElegido] = useState<Elegido | null>(null);
	const [marcando, setMarcando] = useState(false);
	const [enviarYa, setEnviarYa] = useState(false);
	// Como WhatsApp (Esteban, 2026-10-26): al elegir una foto se abre solo el editor de marcas.
	useEffect(() => {
		if (elegido?.tipo === "image" && !elegido.listo && elegido.file instanceof File && !/^image\/(gif|webp)$/i.test(elegido.file.type)) setMarcando(true);
	}, [elegido?.url]);
	// Varios a la vez (Esteban, 2026-10-12): fotos/videos/documentos en tanda; salen uno tras otro, el texto con el primero.
	const [varios, setVarios] = useState<ElegidoVarios[]>([]);
	const [progVarios, setProgVarios] = useState<{ i: number; n: number; f: number } | null>(null);
	const [caption, setCaption] = useState("");
	const cuadroCaption = useRef<HTMLTextAreaElement>(null);
	useCrecer(cuadroCaption, caption, 4);
	const [progreso, setProgreso] = useState<number | null>(null);   // 0..1 subiendo; 1 = el servidor convierte y manda
	const [error, setError] = useState<string | null>(null);
	const [grabando, setGrabando] = useState<number | null>(null);    // segundos
	// acumulado = ms grabados antes del tramo actual; tramo = cuándo empezó el tramo en vivo (null en pausa).
	const rec = useRef<{ mr: any; stream: MediaStream; partes: Blob[]; ext: string; mime: string; timer: any; acumulado: number; tramo: number | null;
		alLlegar: (() => void) | null; final: boolean } | null>(null);
	// En pausa: lo grabado hasta ahí para escucharlo antes de enviar.
	const [previa, setPrevia] = useState<{ url: string; segundos: number } | null>(null);
	const inputMedia = useRef<HTMLInputElement>(null);
	const inputCamara = useRef<HTMLInputElement>(null);
	const inputDoc = useRef<HTMLInputElement>(null);
	const caja = useRef<HTMLDivElement>(null);
	const ultimaNota = useRef<{ blob: Blob; name: string } | null>(null);   // para reintentar si falló el envío

	const ocupado = !!elegido || grabando !== null || varios.length > 0;
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => { onOcupado(ocupado); }, [ocupado]);
	useEffect(() => () => { soltarMicrofono(); }, []);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => () => { if (previa) URL.revokeObjectURL(previa.url); }, [previa]);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => { cancelar(); }, [prospectId]);
	useEffect(() => {
		if (!menu) return;
		const fuera = (e: MouseEvent | TouchEvent) => { if (caja.current && !caja.current.contains(e.target as Node)) setMenu(false); };
		document.addEventListener("mousedown", fuera);
		document.addEventListener("touchstart", fuera);
		return () => { document.removeEventListener("mousedown", fuera); document.removeEventListener("touchstart", fuera); };
	}, [menu]);

	const limpiarElegido = () => { if (elegido?.url) URL.revokeObjectURL(elegido.url); setElegido(null); setCaption(""); };
	const soltarVarios = () => setVarios((vs) => { vs.forEach((v) => v.url && URL.revokeObjectURL(v.url)); return []; });
	const cancelar = () => { soltarMicrofono(); soltarPrevia(); ultimaNota.current = null; setGrabando(null); limpiarElegido(); soltarVarios(); setProgVarios(null); setProgreso(null); setError(null); };
	const aVarios = (fs: File[], kind: Kind): ElegidoVarios[] => fs.map((f, i) => {
		const tipo = kind === "document" ? "document" : tipoDeArchivo(f);
		return { id: Date.now() + "-" + i + "-" + f.name, file: f, tipo, url: tipo === "document" ? null : URL.createObjectURL(f) };
	});
	const agregarVarios = (fs: FileList | File[]) => {
		setVarios((vs) => [...vs, ...aVarios(Array.from(fs), "media")].slice(0, MAX_ARCHIVOS));
	};
	const quitarVario = (id: string) => setVarios((vs) => { const q = vs.find((v) => v.id === id); if (q?.url) URL.revokeObjectURL(q.url); return vs.filter((v) => v.id !== id); });
	const enviarTanda = async (comentario: string) => {
		if (!varios.length) return;
		if (!enviar) prepararSonido();
		setError(null);
		const err = await enviarVarios(varios, comentario, (f, nombre, kind, txt, onProgress) => enviar
			? enviar(f, nombre, kind, txt, onProgress)
			: http.prospectsService.sendWhatsappMedia(campusId, prospectId, f, nombre, kind, { caption: txt || undefined, channelId, onProgress })
				.then((r) => { if (r.data.avisoMatricula && onMatricula) onMatricula(r.data.avisoMatricula); }), setProgVarios);
		setProgVarios(null);
		if (err) { setError(err); onEnviado(); return; }
		soltarVarios();
		onEnviado();
	};

	const elegir = (kind: Kind) => (e: React.ChangeEvent<HTMLInputElement>) => {
		const todos = Array.from(e.target.files || []);
		const f = todos[0];
		e.target.value = "";
		setMenu(false);
		if (!f) return;
		setError(null);
		if (alElegir) { alElegir(todos); return; }
		if (todos.length > 1) {
			limpiarElegido();
			const grandes = todos.filter((x) => x.size > MAX_SUBIDA);
			if (grandes.length) setError(grandes.length === 1 ? "\"" + grandes[0].name + "\" pasa de 200 MB." : grandes.length + " archivos pasan de 200 MB.");
			setVarios((vs) => [...vs, ...aVarios(todos.filter((x) => x.size <= MAX_SUBIDA), kind)].slice(0, MAX_ARCHIVOS));
			return;
		}
		if (f.size > MAX_SUBIDA) { setError("El archivo pasa de 200 MB."); return; }
		const tipo = tipoDe(f, kind);
		limpiarElegido();
		setElegido({ file: f, name: f.name || (tipo === "video" ? "video.mp4" : "foto.jpg"), kind, tipo,
			url: tipo === "document" ? null : URL.createObjectURL(f) });
	};

	/** La foto marcada reemplaza a la original (ya sale a tamaño de envío: no se vuelve a comprimir). */
	const ponerMarcada = (hechas: { id: string; file: File }[]) => {
		const f = hechas[0]?.file;
		if (!f || !elegido) return;
		if (elegido.url) URL.revokeObjectURL(elegido.url);
		setElegido({ ...elegido, file: f, name: f.name, url: URL.createObjectURL(f), listo: true });
	};
	// Enviar desde el editor: en el render siguiente elegido ya trae la foto marcada.
	useEffect(() => { if (enviarYa && elegido) { setEnviarYa(false); subir(elegido.file, elegido.name, elegido.kind, caption); } }, [enviarYa]);

	const subir = async (file: Blob, name: string, kind: Kind, texto: string) => {
		setProgreso(0); setError(null);
		if (!enviar) prepararSonido();
		try {
			// Una foto se achica en el navegador antes de subirla (2026-10-26), salvo que ya venga del editor de marcas.
			if (elegido?.tipo === "image" && !elegido.listo && file instanceof File) {
				try { const f2 = await comprimirFoto(file); file = f2; name = f2.name; } catch (e) { /* se manda tal cual */ }
			}
			if (enviar) await enviar(file, name, kind, texto.trim(), (f) => setProgreso(f));
			else {
				const r = await http.prospectsService.sendWhatsappMedia(campusId, prospectId, file, name, kind,
					{ caption: texto.trim() || undefined, channelId, onProgress: (f) => setProgreso(f) });
				if (r.data.avisoMatricula && onMatricula) onMatricula(r.data.avisoMatricula);
			}
			limpiarElegido(); soltarPrevia(); ultimaNota.current = null; setGrabando(null); setProgreso(null);
			onEnviado();
		} catch (e: any) {
			setProgreso(null);
			setError(e?.response?.status === 413 ? "El archivo es demasiado grande para subirlo." : errorTexto(e));
		}
	};

	// ------------------------------------------------------------ micrófono

	function soltarMicrofono() {
		const r = rec.current;
		if (!r) return;
		clearInterval(r.timer);
		try { if (r.mr.state !== "inactive") { r.mr.onstop = null; r.mr.stop(); } } catch (e) { /* ya parado */ }
		r.stream.getTracks().forEach((t) => t.stop());
		rec.current = null;
	}

	function soltarPrevia() {
		setPrevia((p) => { if (p) URL.revokeObjectURL(p.url); return null; });
	}

	const msGrabados = (r: NonNullable<typeof rec.current>) => r.acumulado + (r.tramo !== null ? Date.now() - r.tramo : 0);
	const blobDe = (r: NonNullable<typeof rec.current>) => new Blob(r.partes, { type: (r.mime || "audio/" + r.ext).split(";")[0] });

	const grabar = async () => {
		setError(null); setMenu(false);
		const MR: any = (window as any).MediaRecorder;
		if (!navigator.mediaDevices?.getUserMedia || !MR) { setError("Este navegador no puede grabar audio."); return; }
		let stream: MediaStream;
		try {
			stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } as any });
		} catch (e: any) {
			setError(e?.name === "NotAllowedError" ? "Permite el micrófono para grabar." : "No se encontró un micrófono.");
			return;
		}
		const { mime, ext } = formatoGrabacion();
		const mr = mime ? new MR(stream, { mimeType: mime, audioBitsPerSecond: 64000 }) : new MR(stream);
		const partes: Blob[] = [];
		mr.ondataavailable = (ev: any) => {
			if (ev.data && ev.data.size) partes.push(ev.data);
			const r = rec.current;
			if (r && r.alLlegar) r.alLlegar();   // en pausa: cada trozo que llega rehace la previa (la de requestData es la última)
		};
		const timer = setInterval(() => {
			const r = rec.current;
			if (!r) return;
			const s = msGrabados(r) / 1000;
			setGrabando(s);
			if (s >= 15 * 60 && r.tramo !== null) pausar();    // tope de 15 min: se pausa para escucharla, no se manda sola
		}, 250);
		rec.current = { mr, stream, partes, ext, mime: mr.mimeType || mime, timer, acumulado: 0, tramo: Date.now(), alLlegar: null, final: false };
		soltarPrevia();
		mr.start(1000);
		setGrabando(0);
	};

	/** Pausa y arma lo grabado para escucharlo. Si el navegador no sabe pausar, termina la grabación (ya no se puede seguir). */
	const pausar = () => {
		const r = rec.current;
		if (!r || r.tramo === null) return;
		r.acumulado += Date.now() - r.tramo;
		r.tramo = null;
		const segundos = r.acumulado / 1000;
		setGrabando(segundos);
		const armar = () => {
			const blob = blobDe(r);
			if (blob.size) setPrevia((p) => { if (p) URL.revokeObjectURL(p.url); return { url: URL.createObjectURL(blob), segundos }; });
		};
		if (typeof r.mr.pause === "function") {
			r.alLlegar = armar;
			try { r.mr.requestData(); r.mr.pause(); } catch (e) { r.alLlegar = null; armar(); }
		} else {
			r.final = true;
			clearInterval(r.timer);
			r.mr.onstop = () => { r.stream.getTracks().forEach((t) => t.stop()); armar(); };
			r.mr.stop();
		}
	};

	const seguir = () => {
		const r = rec.current;
		if (!r || r.tramo !== null || r.final) return;
		r.alLlegar = null;
		soltarPrevia();
		r.tramo = Date.now();
		r.mr.resume();
	};

	const enviarNota = () => {
		const r = rec.current;
		if (!r) {
			if (ultimaNota.current) subir(ultimaNota.current.blob, ultimaNota.current.name, "voice", "");
			return;
		}
		const ms = msGrabados(r);
		const mandar = () => {
			const blob = blobDe(r);
			if (!blob.size || ms < 700) { setError("La grabación salió vacía."); return; }
			ultimaNota.current = { blob, name: "nota-de-voz." + r.ext };
			subir(blob, "nota-de-voz." + r.ext, "voice", "");
		};
		clearInterval(r.timer);
		r.alLlegar = null;
		if (r.final) { rec.current = null; mandar(); return; }
		r.mr.onstop = () => {
			r.stream.getTracks().forEach((t) => t.stop());
			rec.current = null;
			mandar();
		};
		r.mr.stop();
	};

	// ------------------------------------------------------------ vista

	const enviando = progreso !== null;
	const estado = enviando ? (progreso! < 1 ? "Subiendo " + Math.round(progreso! * 100) + " %" : elegido?.tipo === "video" ? "Convirtiendo video…" : "Enviando…") : null;
	const barra = enviando ? <div className="cv-progreso" role="progressbar" aria-valuenow={Math.round((progreso || 0) * 100)}>
		<span style={{ width: Math.max(4, Math.round((progreso || 0) * 100)) + "%" }} className={progreso! >= 1 ? "convirtiendo" : ""} /></div> : null;

	if (grabando !== null) {
		// En vivo: Borrar o Pausar. En pausa: escuchar, seguir grabando, borrar o enviar (nunca sale sola).
		const enPausa = !rec.current || rec.current.tramo === null;
		const puedeSeguir = !!rec.current && !rec.current.final;
		return (
			<div className={"cv-grabando" + (enPausa ? " pausa" : "")}>
				{enviando ? <span className="cv-grab-tiempo">{estado}</span>
					: enPausa && previa ? <Escuchar key={previa.url} url={previa.url} segundos={previa.segundos} />
					: <><span className={"cv-grab-punto" + (enPausa ? "" : " vivo")} aria-hidden="true" /><span className="cv-grab-tiempo">{reloj(grabando)}</span></>}
				{barra}
				{error ? <span className="cv-adj-error">{error}</span> : null}
				<span className="cv-grab-esp" />
				<button className="cv-grab-btn" onClick={cancelar} disabled={enviando && progreso! >= 1} aria-label="Borrar nota de voz" title="Borrar">
					<FontAwesomeIcon icon={faTrash} /><span>Borrar</span>
				</button>
				{enviando ? null : !enPausa ? (
					<button className="cv-grab-btn" onClick={pausar} aria-label="Pausar grabación" title="Pausar">
						<FontAwesomeIcon icon={faPause} /><span>Pausar</span>
					</button>
				) : puedeSeguir ? (
					<button className="cv-grab-btn" onClick={seguir} aria-label="Seguir grabando" title="Seguir grabando">
						<FontAwesomeIcon icon={faMicrophone} /><span>Seguir</span>
					</button>
				) : null}
				{enPausa || enviando ? <button className="cv-enviar wa" onClick={enviarNota} disabled={enviando}>Enviar</button> : null}
			</div>
		);
	}

	if (varios.length) {
		return (
			<BandejaEnvio compacta={esTelefono()} elegidos={varios} onQuitar={quitarVario} onAgregar={agregarVarios} onCancelar={cancelar}
				onEnviar={(c) => { if (puede) enviarTanda(c); }} progreso={progVarios} error={error}
				onCambiar={(id, f) => setVarios((vs) => cambiarElegido(vs, id, f))} />
		);
	}

	if (elegido) {
		return (
			<div className="cv-previa">
				<div className="cv-previa-medio">
					{elegido.tipo === "image" && elegido.url ? (
						<div className="cv-previa-foto">
							<img src={elegido.url} alt="Vista previa" onClick={() => { if (!enviando && elegido.file instanceof File) setMarcando(true); }} />
							{!enviando && elegido.file instanceof File && !/^image\/(gif|webp)$/i.test(elegido.file.type) ? (
								<button type="button" className="eqm-marcar" onClick={() => setMarcando(true)} aria-label="Marcar la foto" title="Marcar la foto (lápiz, flecha, círculo, texto)"><WappIcono n="edit" /></button>
							) : null}
						</div>
					) : null}
					{elegido.tipo === "video" && elegido.url ? <video src={elegido.url} controls playsInline muted /> : null}
					{elegido.tipo === "document" ? <div className="cv-previa-doc"><FontAwesomeIcon icon={faFile} /><b>{elegido.name}</b><span>{tamano(elegido.file.size)}</span></div> : null}
				</div>
				<div className="cv-previa-pie">
					<label htmlFor="cv-caption">Texto</label>
					{/* Enter manda; Shift+Enter (o Ctrl/Alt) baja de renglón (Nancy, 2026-10-15). */}
					<div className="cv-caption-fila">
						<textarea id="cv-caption" ref={cuadroCaption} rows={1} value={caption} onChange={(e) => setCaption(e.target.value)} disabled={enviando} maxLength={1024}
							placeholder="Opcional" onKeyDown={(e) => { teclaEnter(e, () => { if (!enviando) subir(elegido.file, elegido.name, elegido.kind, caption); }); }} />
						<BotonEmoji compacto disabled={enviando} onElegir={(em) => insertarEnCursor(cuadroCaption.current, caption, em, setCaption)} />
					</div>
					{barra}
					{estado ? <span className="cv-adj-estado">{estado}</span> : null}
					{error ? <span className="cv-adj-error">{error}</span> : null}
					<div className="cv-previa-botones">
						<button className="cv-cancelar" onClick={cancelar} disabled={enviando && progreso! >= 1}>Cancelar</button>
						<button className="cv-enviar wa" disabled={enviando || !puede} onClick={() => subir(elegido.file, elegido.name, elegido.kind, caption)}>Enviar</button>
					</div>
				</div>
				{marcando && elegido.file instanceof File && elegido.url ? (
					<MarcarFoto fotos={[{ id: "una", file: elegido.file, url: elegido.url }]} onCerrar={() => setMarcando(false)}
						onListo={(hechas) => { ponerMarcada(hechas); setMarcando(false); }}
						comentario={caption} onComentario={setCaption}
						onEnviar={puede ? (hechas) => { ponerMarcada(hechas); setMarcando(false); setEnviarYa(true); } : undefined} />
				) : null}
			</div>
		);
	}

	return (
		<div className="cv-adj" ref={caja}>
			<button type="button" className="cv-icono" aria-label="Adjuntar" aria-expanded={menu} disabled={!puede} onClick={() => setMenu(!menu)}>
				<FontAwesomeIcon icon={faPaperclip} />
			</button>
			<button type="button" className="cv-icono mic" aria-label="Grabar nota de voz" disabled={!puede} onClick={grabar}>
				<FontAwesomeIcon icon={faMicrophone} />
			</button>
			{menu ? (
				<div className="cv-adj-menu" role="menu">
					{esTelefono() ? <button role="menuitem" onClick={() => inputCamara.current?.click()}><FontAwesomeIcon icon={faCamera} />Cámara</button> : null}
					<button role="menuitem" onClick={() => inputMedia.current?.click()}><FontAwesomeIcon icon={faImage} />Fotos o videos</button>
					<button role="menuitem" onClick={() => inputDoc.current?.click()}><FontAwesomeIcon icon={faFile} />Documentos</button>
				</div>
			) : null}
			{error ? <span className="cv-adj-error flota">{error}</span> : null}
			<input ref={inputCamara} type="file" accept="image/*,video/*" capture="environment" hidden onChange={elegir("media")} />
			<input ref={inputMedia} type="file" accept="image/*,video/*,.heic,.heif" multiple hidden onChange={elegir("media")} />
			<input ref={inputDoc} type="file" multiple hidden onChange={elegir("document")} />
		</div>
	);
};

/** Reproductor de la nota en pausa: play/pausa, avance y tiempo (el webm a medias no trae duración: se usa la grabada). */
const Escuchar: React.FC<{ url: string; segundos: number }> = ({ url, segundos }) => {
	const audio = useRef<HTMLAudioElement>(null);
	const [sonando, setSonando] = useState(false);
	const [t, setT] = useState(0);
	const [falla, setFalla] = useState(false);
	const total = Math.max(segundos, 0.1);
	const tocar = () => {
		const a = audio.current;
		if (!a) return;
		if (sonando) { a.pause(); return; }
		if (a.ended || t >= total) a.currentTime = 0;
		a.play().catch(() => setFalla(true));
	};
	if (falla) return <span className="cv-grab-tiempo">{reloj(segundos)}</span>;
	return (
		<div className="cv-escuchar">
			<button type="button" className="cv-escuchar-play" onClick={tocar} aria-label={sonando ? "Pausar" : "Escuchar"}>
				<FontAwesomeIcon icon={sonando ? faPause : faPlay} />
			</button>
			<span className="cv-escuchar-barra" aria-hidden="true"><span style={{ width: Math.min(100, (t / total) * 100) + "%" }} /></span>
			<span className="cv-escuchar-t">{reloj(sonando || t > 0 ? Math.min(t, total) : total)}</span>
			<audio ref={audio} src={url} preload="auto" onPlay={() => setSonando(true)} onPause={() => setSonando(false)}
				onEnded={() => { setSonando(false); setT(0); }} onTimeUpdate={(e) => setT(e.currentTarget.currentTime)} onError={() => setFalla(true)} />
		</div>
	);
};

export default Adjuntos;
